import { POS_PROVIDERS, normalizeProvider, getPosProvider } from "../pos/providers";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, collection, doc, getDoc, getDocs, query, orderBy, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";

import Confirm from "../components/Confirm";
import { CASHUP_TAB_KEYS, DEFAULT_ROLE_TAB_DEFAULTS } from "./accessDefaults";
import { MATRIXOS_PRODUCTS } from "../platformProducts";
import { LEAGUE_ROLES, leaguePermissionsForRole, normalizeLeagueRole } from "../league/access/leaguePermissions";

const PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
];

const REGION = "us-central1";
const db = getFirestore();

const MEMBER_APP_OPTIONS = MATRIXOS_PRODUCTS.map(({ id, label }) => ({ id, label }));

function normalizeAppId(app) {
  const v = String(app || "").trim().toLowerCase();
  if (["mc", "morphius", "morpheus-count"].includes(v)) return "morpheus";
  if (["neo", "admin", "cash-up"].includes(v)) return "cashup";
  if (["matrix-league", "matrix_league"].includes(v)) return "league";
  return v;
}

function normalizeApps(input) {
  if (Array.isArray(input)) {
    return uniqStrings(input.map(normalizeAppId));
  }
  if (input && typeof input === "object") {
    return uniqStrings(
      Object.entries(input)
        .filter(([, value]) => value === true)
        .map(([key]) => normalizeAppId(key))
    );
  }
  return [];
}

function normalizeRole(role) {
  const r = String(role || "").trim().toLowerCase();
  if (r === "owner" || r === "admin" || r === "viewer" || r === "kiosk") return r;
  return "viewer";
}

function appsArrayToMap(arr) {
  const selected = new Set(normalizeApps(arr));
  // Always write explicit true/false flags so Firestore merge/update cannot leave
  // old enabled apps stuck as true after a Super Admin toggles them off.
  return MEMBER_APP_OPTIONS.reduce((out, app) => {
    out[app.id] = selected.has(app.id);
    return out;
  }, {});
}

function resolveOrgApps(...sources) {
  for (const source of sources) {
    const normalized = normalizeApps(source);
    if (normalized.length) return normalized;
  }
  return ["cashup"];
}


async function ensureLeaguePlatformArchitecture({ orgId, enabled = false }) {
  if (!orgId || !enabled) return;

  const now = serverTimestamp();
  await setDoc(doc(db, "orgs", orgId, "league", "platform"), {
    productId: "league",
    architectureVersion: "LA4",
    enabled: true,
    lifecycleStatus: "active",
    dataBoundary: `orgs/${orgId}/league`,
    collections: ["seasons", "clubs", "fixtures", "standings", "results", "awards", "history"],
    configurationOwner: "organization_league_admin",
    updatedAt: now,
  }, { merge: true });
}

function isActiveDoc(x) {
  return !(x?.isArchived === true || x?.archived === true || x?.disabled === true || x?.isDisabled === true);
}

function storeLabel(s) {
  return s?.name || s?.label || s?.title || s?.storeName || s?.id || "Unknown store";
}

function uniqStrings(arr) {
  return Array.from(new Set((arr || []).map((x) => String(x || "").trim()).filter(Boolean)));
}

function memberIdentityKey(m = {}) {
  const email = String(m.email || "").trim().toLowerCase();
  if (email) return `email:${email}`;
  const uid = String(m.uid || m.userId || m.authUid || "").trim();
  if (uid) return `uid:${uid}`;
  return `doc:${String(m.id || "")}`;
}

function memberDocScore(m = {}) {
  const id = String(m.id || "").trim();
  const uid = String(m.uid || m.userId || m.authUid || "").trim();
  const email = String(m.email || "").trim().toLowerCase();
  let score = 0;
  if (uid && id === uid) score += 100;
  if (uid) score += 40;
  if (email && id !== email && !id.includes("_gmail_com") && !id.includes("_co_za")) score += 20;
  if (normalizeApps(m.apps || m.appsMap || m.appAccess).length) score += 10;
  if (m.allStores === true || m.accessAllStores === true) score += 5;
  if (isActiveDoc(m)) score += 5;
  return score;
}

function dedupeOrgMembers(items = []) {
  const grouped = new Map();
  for (const item of items || []) {
    if (!isActiveDoc(item)) continue;
    const key = memberIdentityKey(item);
    const existing = grouped.get(key);
    if (!existing || memberDocScore(item) > memberDocScore(existing)) {
      grouped.set(key, item);
    }
  }

  // Merge duplicated email docs into the preferred display record so the UI does
  // not show the same person twice, while preserving access flags from either
  // legacy email-id docs or newer UID docs.
  const byKey = new Map();
  for (const item of items || []) {
    if (!isActiveDoc(item)) continue;
    const key = memberIdentityKey(item);
    const preferred = grouped.get(key);
    if (!preferred) continue;
    const current = byKey.get(key) || preferred;
    byKey.set(key, {
      ...current,
      apps: uniqStrings([...(normalizeApps(current.apps || current.appsMap || current.appAccess)), ...(normalizeApps(item.apps || item.appsMap || item.appAccess))]),
      allStores: current.allStores === true || current.accessAllStores === true || item.allStores === true || item.accessAllStores === true,
      accessAllStores: current.allStores === true || current.accessAllStores === true || item.allStores === true || item.accessAllStores === true,
      storeIds: uniqStrings([...(current.storeIds || []), ...(item.storeIds || []), ...(item.allowedStoreIds || []), ...(item.visibleStoreIds || []), ...(item.cashupStoreIds || [])]),
      allowedStoreIds: uniqStrings([...(current.allowedStoreIds || []), ...(item.allowedStoreIds || []), ...(item.storeIds || [])]),
      visibleStoreIds: uniqStrings([...(current.visibleStoreIds || []), ...(item.visibleStoreIds || []), ...(item.storeIds || [])]),
      cashupStoreIds: uniqStrings([...(current.cashupStoreIds || []), ...(item.cashupStoreIds || []), ...(item.storeIds || [])]),
    });
  }

  return Array.from(byKey.values());
}


function morpheusProfileForMember({ email = "", displayName = "" } = {}) {
  const cleanEmail = String(email || "").trim().toLowerCase();
  if (!cleanEmail) return null;
  const safeId = `mc-member-${cleanEmail.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 80)}`;
  const baseName = String(displayName || cleanEmail.split("@")[0] || "Operator").trim();
  return {
    id: safeId,
    email: cleanEmail,
    name: baseName,
    surname: "",
    pin: "000000",
    store: "ALL",
    role: "user",
    accessLevel: "standard",
    operationalRole: "Stock Controller",
    allowedSiteIds: [],
    assignedSectionIds: [],
    tabs: ["workspace", "imports", "prepared-data", "sessions", "archive"],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

async function syncMorpheusProfileForMember({ orgId, email, displayName, apps = [] }) {
  const selectedApps = normalizeApps(apps);
  if (!selectedApps.includes("morpheus")) return;
  const provisioned = morpheusProfileForMember({ email, displayName });
  if (!provisioned || !orgId) return;

  const ref = doc(db, "orgs", orgId, "morpheus", "profiles");
  const snap = await getDoc(ref);
  let current = [];
  if (snap.exists()) {
    const data = snap.data() || {};
    if (Array.isArray(data.json)) current = data.json;
    else {
      try { current = JSON.parse(String(data.value || "[]")); } catch (_) { current = []; }
    }
  }
  const existing = current.find((item) => {
    const itemEmail = String(item?.email || item?.userEmail || "").trim().toLowerCase();
    return itemEmail === provisioned.email;
  });

  // Super Admin controls whether Morpheus is enabled for the organisation member,
  // but it does not control the identity selected inside Morpheus. Access level,
  // operational role, site scope, sections, tabs and PIN remain owned exclusively
  // by the Morpheus Access Layer/People directory.
  const activatedProfile = existing
    ? {
        ...existing,
        loginInviteRequired: false,
        loginAccessStatus: "active",
        loginActivatedAt: existing?.loginActivatedAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    : {
        ...provisioned,
        loginInviteRequired: false,
        loginAccessStatus: "active",
        loginActivatedAt: new Date().toISOString(),
      };
  const next = [
    ...current.filter((item) => {
      const itemEmail = String(item?.email || item?.userEmail || "").trim().toLowerCase();
      return String(item?.id || "") !== String(existing?.id || activatedProfile.id || "")
        && itemEmail !== provisioned.email;
    }),
    activatedProfile,
  ];
  await setDoc(ref, {
    localKey: "mc-profiles",
    value: JSON.stringify(next),
    json: next,
    updatedAt: serverTimestamp(),
    updatedByEmail: "super-admin",
  }, { merge: true });
}

async function syncLegacyAccessDocs({ uid, email, displayName, role, orgId, allStores, storeIds, apps = ["cashup"] }) {
  const cleanUid = String(uid || "").trim();
  if (!cleanUid) return;
  const cleanEmail = String(email || "").trim().toLowerCase();
  const cleanRole = normalizeRole(role);
  const cleanName = String(displayName || "").trim() || cleanEmail.split("@")[0] || cleanUid;
  const selectedStoreIds = uniqStrings(storeIds || []);
  const selectedApps = normalizeApps(apps);
  const payload = {
    email: cleanEmail,
    name: cleanName,
    displayName: cleanName,
    role: cleanRole,
    orgId: String(orgId || "").trim(),
    isAdmin: cleanRole === "owner" || cleanRole === "admin",
    disabled: false,
    storeIds: allStores ? [] : selectedStoreIds,
    apps: selectedApps,
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, "managers", cleanUid), {
    ...payload,
    createdAt: serverTimestamp(),
  }, { merge: true });

  await setDoc(doc(db, "userRoles", cleanUid), {
    uid: cleanUid,
    email: cleanEmail,
    name: cleanName,
    displayName: cleanName,
    role: cleanRole,
    orgId: String(orgId || "").trim(),
    isAdmin: cleanRole === "owner" || cleanRole === "admin",
    disabled: false,
    allStores: !!allStores,
    accessAllStores: !!allStores,
    storeIds: allStores ? [] : selectedStoreIds,
    allowedStoreIds: allStores ? [] : selectedStoreIds,
    visibleStoreIds: allStores ? [] : selectedStoreIds,
    cashupStoreIds: allStores ? [] : selectedStoreIds,
    apps: selectedApps,
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  }, { merge: true });
}

export default function SuperOrgDetails() {
  const { orgId } = useParams();
  const nav = useNavigate();

  const fns = useMemo(() => getFunctions(undefined, REGION), []);

  const [org, setOrg] = useState(null);
  const [roleTabDefaults, setRoleTabDefaults] = useState(DEFAULT_ROLE_TAB_DEFAULTS);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [province, setProvince] = useState("");
  const [timezone, setTimezone] = useState("Africa/Johannesburg");
  const [businessMode, setBusinessMode] = useState("hospitality_retail");
  const [appVariant, setAppVariant] = useState("shared_device");
  const [apps, setApps] = useState(["cashup"]);
  const [defaultApp, setDefaultApp] = useState("cashup");
  const [primaryOwnerEmail, setPrimaryOwnerEmail] = useState("");
  const [savingPrimaryOwner, setSavingPrimaryOwner] = useState(false);
  const [resetPrimaryOwnerPinOpen, setResetPrimaryOwnerPinOpen] = useState(false);
  const [resettingPrimaryOwnerPin, setResettingPrimaryOwnerPin] = useState(false);
  const [primaryOwnerPinStatus, setPrimaryOwnerPinStatus] = useState("");

  const [members, setMembers] = useState([]);
  const [morpheusLoginRequests, setMorpheusLoginRequests] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);

  const [tabsModalOpen, setTabsModalOpen] = useState(false);
  const [tabsModalUser, setTabsModalUser] = useState(null);
  const [tabsDraft, setTabsDraft] = useState({});
  const [savingTabs, setSavingTabs] = useState(false);

  const [editMemberOpen, setEditMemberOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editRole, setEditRole] = useState("admin");
  const [editStoreIds, setEditStoreIds] = useState([]);
  const [editAllStores, setEditAllStores] = useState(false);
  const [editApps, setEditApps] = useState(["cashup"]);
  const [editLeagueRole, setEditLeagueRole] = useState("viewer");
  const [savingMember, setSavingMember] = useState(false);
  const [editResetLoading, setEditResetLoading] = useState(false);
  const [editResetStatus, setEditResetStatus] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("admin");
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [editTrialLink, setEditTrialLink] = useState("");
  const [editTrialStatus, setEditTrialStatus] = useState("");
  const [editContractLink, setEditContractLink] = useState("");
  const [editContractStatus, setEditContractStatus] = useState("");

  const [posLoading, setPosLoading] = useState(false);
  const [posSaving, setPosSaving] = useState(false);
  const [posProvider, setPosProvider] = useState("lightspeed");
  const [posEnabled, setPosEnabled] = useState(false);
  const [posStatus, setPosStatus] = useState("not_connected");
  const [posNotes, setPosNotes] = useState("");
  const [posLastUpdated, setPosLastUpdated] = useState("");


  function getTrialAgreementBaseUrl() {
    if (typeof window === "undefined") return "/trial-agreement";
    return `${window.location.origin}/trial-agreement`;
  }
  function getClientContractBaseUrl() {
    if (typeof window === "undefined") return "/contract";
    return `${window.location.origin}/contract`;
  }

  function getResetActionSettings() {
    return undefined;
  }


  async function copyTrialAgreementLinkForEditMember() {
    if (!editMember) return;
    setEditTrialStatus("Generating secure link…");
    try {
      const createInvite = httpsCallable(fns, "createTrialAgreementInvite");
      const email = String(editMember?.email || "").trim().toLowerCase();
      const fullName = String(editMember?.displayName || editMember?.name || "").trim();
      const companyName = String(org?.name || "").trim();
      const baseUrl = getTrialAgreementBaseUrl();

      const res = await createInvite({
        orgId,
        email,
        fullName,
        companyName,
        baseUrl,
        sendEmail: true,
      });

      const link = String(res?.data?.link || "").trim();
      const emailed = res?.data?.emailed === true || res?.data?.emailSent === true;
      if (!link) throw new Error("No secure link was returned.");

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setEditTrialStatus(emailed ? "Trial email queued and secure link copied." : "Secure trial link copied.");
      } else {
        setEditTrialStatus(emailed ? "Trial email queued. Copy the secure link manually below if needed." : "Secure link created. Copy it manually below.");
      }
      setEditTrialLink(link);
    } catch (e) {
      console.error("[SuperOrgDetails] createTrialAgreementInvite failed", e);
      setEditTrialStatus(e?.message || "Could not generate secure trial link.");
    }
  }

  async function copyClientContractLinkForEditMember() {
    if (!editMember) return;
    setEditContractStatus("Generating secure contract link…");
    try {
      const createInvite = httpsCallable(fns, "createClientContractInvite");
      const email = String(editMember?.email || "").trim().toLowerCase();
      const fullName = String(editMember?.displayName || editMember?.name || "").trim();
      const companyName = String(org?.name || "").trim();
      const baseUrl = getClientContractBaseUrl();

      const res = await createInvite({
        orgId,
        email,
        fullName,
        companyName,
        baseUrl,
        productName: "Neo Platform",
        billingModel: "Per store",
        trialDays: 30,
        sendEmail: true,
      });

      const link = String(res?.data?.link || "").trim();
      const emailed = res?.data?.emailed === true || res?.data?.emailSent === true;
      if (!link) throw new Error("No secure contract link was returned.");

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setEditContractStatus(emailed ? "Contract email queued and secure link copied." : "Secure contract link copied.");
      } else {
        setEditContractStatus(emailed ? "Contract email queued. Copy the secure link manually below if needed." : "Secure contract link created. Copy it manually below.");
      }
      setEditContractLink(link);
    } catch (e) {
      console.error("[SuperOrgDetails] createClientContractInvite failed", e);
      setEditContractStatus(e?.message || "Could not generate secure contract link.");
    }
  }


  function clearBanner() {
    setErr("");
    setNotice("");
  }

  const normalizePosProvider = normalizeProvider;
  const formatPosProvider = value => getPosProvider(value).label;

  function normalizePosStatus(status) {
    const value = String(status || "").trim().toLowerCase();
    if (["connected", "ready", "synced", "active"].includes(value)) return "connected";
    if (["expired", "token_expired", "reconnect_required"].includes(value)) return "token_expired";
    if (["error", "failed"].includes(value)) return "error";
    if (["pending", "setup_pending", "oauth_pending"].includes(value)) return "setup_pending";
    return "not_connected";
  }

  function posStatusMeta(status, enabled = posEnabled) {
    if (!getPosProvider(posProvider).available) return { label: "Setup pending", className: "border-cyan-400/40 bg-cyan-500/15 text-cyan-100" };
    if (!enabled) return { label: "Disabled", className: "border-white/10 bg-white/5 text-white/60" };
    const normalized = normalizePosStatus(status);
    if (normalized === "connected") return { label: "Connected", className: "border-emerald-400/40 bg-emerald-500/15 text-emerald-200 shadow-[0_0_18px_rgba(16,185,129,0.25)]" };
    if (normalized === "setup_pending") return { label: "Setup pending", className: "border-cyan-400/40 bg-cyan-500/15 text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.18)]" };
    if (normalized === "token_expired") return { label: "Token expired", className: "border-amber-400/40 bg-amber-500/15 text-amber-200 shadow-[0_0_18px_rgba(245,158,11,0.2)]" };
    if (normalized === "error") return { label: "Error", className: "border-rose-400/40 bg-rose-500/15 text-rose-200 shadow-[0_0_18px_rgba(244,63,94,0.2)]" };
    return { label: "Not connected", className: "border-white/10 bg-white/5 text-white/70" };
  }

  async function loadPosIntegration() {
    if (!orgId) return;
    setPosLoading(true);
    try {
      const snap = await getDoc(doc(db, "orgs", orgId, "integrations", "pos"));
      const data = snap.exists() ? snap.data() || {} : {};
      setPosProvider(normalizePosProvider(data.provider || "lightspeed"));
      setPosEnabled(data.enabled === false ? false : (data.enabled === true || data.mode === "pos" || normalizePosStatus(data.status) !== "not_connected"));
      setPosStatus(normalizePosStatus(data.status || (data.enabled ? "setup_pending" : "not_connected")));
      setPosNotes(String(data.notes || ""));
      setPosLastUpdated(String(data.updatedAtText || data.lastStatusAt || data.updatedAt || ""));
    } catch (e) {
      console.error("[SuperOrgDetails] load POS integration failed", e);
    } finally {
      setPosLoading(false);
    }
  }

  async function savePosIntegration(next = {}) {
    if (!orgId) return;
    clearBanner();
    setPosSaving(true);
    try {
      const provider = normalizePosProvider(next.provider ?? posProvider);
      const configure = httpsCallable(fns, "configurePosIntegration");
      const result = await configure({orgId, provider, notes:String(next.notes ?? posNotes ?? ""), action:next.enabled === false ? "disable" : "save"});
      const payload = result.data;
      const enabled = payload.enabled === true;
      setPosProvider(provider);
      setPosEnabled(enabled);
      setPosStatus(payload.status);
      setPosNotes(payload.notes);
      setPosLastUpdated(payload.updatedAtText);
      setNotice(payload.status === "setup_pending" ? "Planned provider saved. Connection and sync remain unavailable until its connector is ready." : enabled ? "POS integration setup saved." : "POS integration disabled. MC can continue with CSV fallback.");
    } catch (e) {
      setErr(e?.message || "Failed to save POS integration setup.");
    } finally {
      setPosSaving(false);
    }
  }

  async function connectLightspeedOAuth() {
    if (!orgId) return;
    clearBanner();
    setPosSaving(true);
    try {
      const provider = normalizePosProvider(posProvider);
      if (!getPosProvider(provider).available) {
        await savePosIntegration({provider, notes:posNotes || getPosProvider(provider).setupMessage});
        return;
      }
      const startOAuth = httpsCallable(fns, "startPosOAuth");
      const returnUrl = typeof window !== "undefined" ? window.location.href : undefined;
      const res = await startOAuth({
        orgId,
        provider,
        returnUrl,
      });

      const authorizationUrl = String(
        res?.data?.authorizationUrl ||
        res?.data?.authUrl ||
        res?.data?.url ||
        ""
      ).trim();

      if (!authorizationUrl) {
        throw new Error("The POS approval link was not returned.");
      }

      setPosProvider(provider);
      setPosEnabled(true);
      setPosStatus("setup_pending");
      setNotice(`Opening ${formatPosProvider(provider)} approval…`);
      window.location.assign(authorizationUrl);
    } catch (e) {
      console.error("[SuperOrgDetails] Lightspeed OAuth start failed", e);
      setErr(e?.message || "Failed to open Lightspeed approval.");
    } finally {
      setPosSaving(false);
    }
  }

  async function disconnectPosIntegration() {
    await savePosIntegration({ enabled: false, status: "not_connected", notes: "Disconnected from Super Admin." });
  }

  useEffect(() => {
    let alive = true;

    (async () => {
      setErr("");
      setLoadingOrg(true);
      try {
        const listOrgs = httpsCallable(fns, "listOrgs");
        const res = await listOrgs({ includeDisabled: true });
        const item = (res?.data?.items || []).find((o) => o.id === orgId);

        if (!alive) return;
        if (!item) {
          setOrg(null);
          return;
        }

        setOrg(item);

        let orgDocData = {};
        try {
          const snap = await getDoc(doc(db, "orgs", orgId));
          orgDocData = snap.exists() ? snap.data() || {} : {};
          const fromDoc = orgDocData?.cashupRoleTabDefaults;
          if (fromDoc && typeof fromDoc === "object") {
            setRoleTabDefaults((prev) => ({ ...prev, ...fromDoc }));
          } else {
            setRoleTabDefaults(DEFAULT_ROLE_TAB_DEFAULTS);
          }
        } catch {
          setRoleTabDefaults(DEFAULT_ROLE_TAB_DEFAULTS);
        }

        const activeApps = resolveOrgApps(orgDocData.apps, orgDocData.enabledApps, item.apps, item.enabledApps);
        const activeDefaultApp = normalizeAppId(orgDocData.defaultApp || item.defaultApp || activeApps[0] || "cashup");

        setApps(activeApps);
        setDefaultApp(activeApps.includes(activeDefaultApp) ? activeDefaultApp : activeApps[0] || "cashup");
        setName(item.name || orgDocData.name || "");
        setProvince(item.province || orgDocData.province || "");
        setTimezone(item.timezone || orgDocData.timezone || "Africa/Johannesburg");
        setBusinessMode(item.businessMode || orgDocData.businessMode || "hospitality_retail");
        setAppVariant(item.appVariant || orgDocData.appVariant || (((item.businessMode || orgDocData.businessMode) === "security_services" || (item.businessMode || orgDocData.businessMode) === "cleaning_services" || (item.businessMode || orgDocData.businessMode) === "field_services") ? "personal_device" : "shared_device"));
        setPrimaryOwnerEmail(String(orgDocData.primaryOwnerEmail || item.primaryOwnerEmail || "").trim().toLowerCase());
      } catch (e) {
        if (alive) setErr(e?.message || "Failed to load org");
      } finally {
        if (alive) setLoadingOrg(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [fns, orgId]);

  useEffect(() => {
    loadPosIntegration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadStores = async () => {
    setLoadingStores(true);
    try {
      const buckets = ["stores", "cashup_stores"];
      const map = new Map();
      for (const bucket of buckets) {
        try {
          const snap = await getDocs(collection(db, "orgs", orgId, bucket));
          snap.docs.forEach((d) => {
            const data = d.data() || {};
            if (!isActiveDoc(data)) return;
            const id = d.id;
            const prev = map.get(id) || {};
            map.set(id, {
              id,
              ...prev,
              ...data,
              name: data.name || data.label || data.title || data.storeName || prev.name || id,
            });
          });
        } catch {
          // ignore missing path
        }
      }
      const items = Array.from(map.values()).sort((a, b) => storeLabel(a).localeCompare(storeLabel(b)));
      setStores(items);
    } finally {
      setLoadingStores(false);
    }
  };

  const reloadMembers = async () => {
    setLoadingMembers(true);
    setErr("");
    try {
      // Do not orderBy("email") here. Some existing/fallback member docs use the
      // email address as the document ID and may be missing an email field, and
      // Firestore orderBy excludes documents where that field is missing.
      const snap = await getDocs(collection(db, "orgs", orgId, "members"));
      try {
        const profileSnap = await getDoc(doc(db, "orgs", orgId, "morpheus", "profiles"));
        const profileData = profileSnap.exists() ? (profileSnap.data() || {}) : {};
        let profileItems = Array.isArray(profileData.json) ? profileData.json : [];
        if (!profileItems.length) { try { profileItems = JSON.parse(String(profileData.value || "[]")); } catch (_) { profileItems = []; } }
        const memberEmails = new Set(snap.docs.map((d) => String(d.data()?.email || (String(d.id).includes("@") ? d.id : "")).trim().toLowerCase()).filter(Boolean));
        setMorpheusLoginRequests(profileItems.filter((profile) => {
          const access = String(profile?.accessLevel || "").toLowerCase();
          const email = String(profile?.email || profile?.userEmail || "").trim().toLowerCase();
          return profile?.loginInviteRequired === true && email && (access === "administrator" || access === "owner") && !memberEmails.has(email);
        }));
      } catch (_) { setMorpheusLoginRequests([]); }
      const items = snap.docs.map((d) => {
        const data = d.data() || {};
        const idEmail = String(d.id || "").includes("@") ? String(d.id).toLowerCase() : "";
        return { id: d.id, ...data, email: String(data.email || idEmail || "").toLowerCase() };
      });

      if (items.length === 0) {
        try {
          const list = httpsCallable(fns, "listOrgMembers");
          const res = await list({ orgId });
          const legacy = (res?.data?.items || []).map((m) => ({
            id: m.id || m.email || m.uid || String(Math.random()),
            email: (m.email || "").toLowerCase(),
            displayName: m.displayName || m.name || m.label || "",
            role: normalizeRole(m.role || (m.isAdmin ? "admin" : "viewer")),
            uid: m.uid || m.userId || m.authUid || "",
            allStores: m.allStores === true || m.accessAllStores === true,
            storeIds: uniqStrings(m.storeIds || m.allowedStoreIds || m.visibleStoreIds || m.cashupStoreIds || []),
          }));
          setMembers(legacy);
          return;
        } catch {
          // ignore
        }
      }

      const normalizedMembers = items.map((m) => {
        const idEmail = String(m.id || "").includes("@") ? String(m.id).toLowerCase() : "";
        const email = String(m.email || idEmail || "").toLowerCase();
        return {
          ...m,
          email,
          displayName: m.displayName || m.name || m.label || (email ? email.split("@")[0] : ""),
          role: normalizeRole(m.role || (m.isAdmin ? "admin" : "viewer")),
          uid: m.uid || m.userId || m.authUid || "",
          allStores: m.allStores === true || m.accessAllStores === true,
          storeIds: uniqStrings(m.storeIds || m.allowedStoreIds || m.visibleStoreIds || m.cashupStoreIds || []),
        };
      });

      setMembers(
        dedupeOrgMembers(normalizedMembers)
          .sort((a, b) => String(a.displayName || a.email || a.id).localeCompare(String(b.displayName || b.email || b.id)))
      );
    } catch (e) {
      setErr(e?.message || "Failed to load members");
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    reloadMembers();
    loadStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);


  function toggleOrgApp(appId) {
    const normalized = normalizeAppId(appId);
    setApps((prev) => {
      const current = normalizeApps(prev);
      if (current.includes(normalized)) {
        if (current.length === 1) return current;
        const next = current.filter((x) => x !== normalized);
        if (!next.includes(defaultApp)) setDefaultApp(next[0] || "cashup");
        return next;
      }
      return normalizeApps([...current, normalized]);
    });
  }

  function toggleEditApp(appId) {
    const normalized = normalizeAppId(appId);
    setEditApps((prev) => {
      const current = normalizeApps(prev);
      if (current.includes(normalized)) {
        if (current.length === 1) return current;
        return current.filter((x) => x !== normalized);
      }
      return normalizeApps([...current, normalized]);
    });
  }

  async function savePrimaryOwner() {
    const email = String(primaryOwnerEmail || '').trim().toLowerCase();
    if (!email || !orgId) {
      setErr('Select a member email as the Primary Owner.');
      return;
    }
    setSavingPrimaryOwner(true);
    clearBanner();
    try {
      await setDoc(doc(db, 'orgs', orgId), { primaryOwnerEmail: email, updatedAt: serverTimestamp() }, { merge: true });
      await setDoc(doc(db, 'orgs', orgId, 'morpheus', 'primaryOwnerEmail'), { localKey: 'mc-primary-owner-email', value: email, json: email, updatedAt: serverTimestamp(), updatedByEmail: 'super-admin' }, { merge: true });

      const profileRef = doc(db, 'orgs', orgId, 'morpheus', 'profiles');
      const snap = await getDoc(profileRef);
      let current = [];
      if (snap.exists()) {
        const data = snap.data() || {};
        if (Array.isArray(data.json)) current = data.json;
        else { try { current = JSON.parse(String(data.value || '[]')); } catch (_) { current = []; } }
      }
      const member = members.find((m) => String(m.email || '').trim().toLowerCase() === email);
      const profileId = `mc-admin-${email.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 80)}`;
      let found = false;
      const next = current.map((item) => {
        const itemEmail = String(item?.email || item?.userEmail || '').trim().toLowerCase();
        const isPrimary = itemEmail === email;
        if (isPrimary) found = true;
        const accessLevel = isPrimary ? 'owner' : (item.accessLevel || (String(item.role || '').toLowerCase() === 'admin' ? 'administrator' : 'standard'));
        return {
          ...item,
          isPrimaryOwner: isPrimary,
          primaryOwner: isPrimary,
          accessLevel,
          operationalRole: isPrimary ? 'Owner' : (item.operationalRole || (accessLevel === 'owner' ? 'Owner' : 'Stock Controller')),
          role: accessLevel === 'owner' || accessLevel === 'administrator' ? 'admin' : 'user',
          store: isPrimary ? 'ALL' : item.store,
          allowedSiteIds: isPrimary ? ['ALL'] : item.allowedSiteIds,
        };
      });
      if (!found) {
        next.unshift({
          id: profileId,
          email,
          name: String(member?.displayName || member?.name || email.split('@')[0] || 'Owner').trim(),
          surname: '',
          pin: '000000',
          store: 'ALL',
          allowedSiteIds: ['ALL'],
          role: 'admin',
          accessLevel: 'owner',
          operationalRole: 'Owner',
          isPrimaryOwner: true,
          primaryOwner: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      await setDoc(profileRef, { localKey: 'mc-profiles', value: JSON.stringify(next), json: next, updatedAt: serverTimestamp(), updatedByEmail: 'super-admin' }, { merge: true });
      setNotice(`Primary Owner saved: ${email}`);
    } catch (e) {
      setErr(e?.message || 'Failed to save Primary Owner');
    } finally {
      setSavingPrimaryOwner(false);
    }
  }

  async function resetPrimaryOwnerPin() {
    const email = String(primaryOwnerEmail || "").trim().toLowerCase();
    if (!email || !orgId) {
      setErr("Select and save a Primary Owner before resetting the PIN.");
      setResetPrimaryOwnerPinOpen(false);
      return;
    }

    setResettingPrimaryOwnerPin(true);
    clearBanner();
    setPrimaryOwnerPinStatus("");
    try {
      const profileRef = doc(db, "orgs", orgId, "morpheus", "profiles");
      const snap = await getDoc(profileRef);
      let current = [];
      if (snap.exists()) {
        const data = snap.data() || {};
        if (Array.isArray(data.json)) current = data.json;
        else {
          try { current = JSON.parse(String(data.value || "[]")); } catch (_) { current = []; }
        }
      }

      const member = members.find((item) => String(item?.email || "").trim().toLowerCase() === email);
      const profileId = `mc-admin-${email.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 80)}`;
      const nowIso = new Date().toISOString();
      const directIndex = current.findIndex((item) => {
        const itemEmail = String(item?.email || item?.userEmail || "").trim().toLowerCase();
        return itemEmail === email || String(item?.id || "") === profileId;
      });
      const protectedIndex = directIndex >= 0 ? directIndex : current.findIndex((item) => item?.isPrimaryOwner === true || item?.primaryOwner === true);
      let found = protectedIndex >= 0;

      const next = current.map((item, index) => {
        if (index !== protectedIndex) return item;
        return {
          ...item,
          id: item.id || profileId,
          email,
          pin: "000000",
          pinResetAt: nowIso,
          pinResetBy: "super-admin",
          requirePinChange: true,
          isPrimaryOwner: true,
          primaryOwner: true,
          accessLevel: "owner",
          operationalRole: "Owner",
          role: "admin",
          store: "ALL",
          allowedSiteIds: ["ALL"],
          updatedAt: nowIso,
        };
      });

      if (!found) {
        next.unshift({
          id: profileId,
          email,
          name: String(member?.displayName || member?.name || email.split("@")[0] || "Owner").trim(),
          surname: "",
          pin: "000000",
          pinResetAt: nowIso,
          pinResetBy: "super-admin",
          requirePinChange: true,
          store: "ALL",
          allowedSiteIds: ["ALL"],
          role: "admin",
          accessLevel: "owner",
          operationalRole: "Owner",
          isPrimaryOwner: true,
          primaryOwner: true,
          createdAt: nowIso,
          updatedAt: nowIso,
        });
      }

      await setDoc(profileRef, {
        localKey: "mc-profiles",
        value: JSON.stringify(next),
        json: next,
        updatedAt: serverTimestamp(),
        updatedByEmail: "super-admin",
      }, { merge: true });

      await setDoc(doc(db, "orgs", orgId, "morpheus", "primaryOwnerPinStatus"), {
        localKey: "mc-primary-owner-pin-status",
        email,
        resetAt: serverTimestamp(),
        resetBy: "super-admin",
        requirePinChange: true,
      }, { merge: true });

      setPrimaryOwnerPinStatus("PIN reset to 000000. The owner must change it after signing in.");
      setNotice(`Primary Owner PIN reset for ${email}`);
      setResetPrimaryOwnerPinOpen(false);
    } catch (e) {
      setErr(e?.message || "Failed to reset Primary Owner PIN");
    } finally {
      setResettingPrimaryOwnerPin(false);
    }
  }

  async function saveOrg() {
    if (!orgId) return;
    clearBanner();
    setSaving(true);
    try {
      const selectedApps = normalizeApps(apps);
      const safeApps = selectedApps.length ? selectedApps : ["cashup"];
      const safeDefaultApp = safeApps.includes(normalizeAppId(defaultApp)) ? normalizeAppId(defaultApp) : safeApps[0];
      const patch = {
        name: String(name || "").trim(),
        province: String(province || "").trim(),
        timezone: String(timezone || "").trim(),
        businessMode: String(businessMode || "hospitality_retail").trim(),
        appVariant: String(appVariant || "shared_device").trim(),
        apps: appsArrayToMap(safeApps),
        enabledApps: safeApps,
        defaultApp: safeDefaultApp,
      };

      try {
        const updateOrg = httpsCallable(fns, "updateOrg");
        await updateOrg({ orgId, patch });
      } catch {
        await updateDoc(doc(db, "orgs", orgId), {
          ...patch,
          updatedAt: serverTimestamp(),
        });
      }

      const listOrgs = httpsCallable(fns, "listOrgs");
      const res = await listOrgs({ includeDisabled: true });
      const item = (res?.data?.items || []).find((o) => o.id === orgId);
      if (item) setOrg(item);
      await ensureLeaguePlatformArchitecture({ orgId, enabled: safeApps.includes("league") });

      setApps(safeApps);
      setDefaultApp(safeDefaultApp);
      setNotice("Saved!");
    } catch (e) {
      setErr(e?.message || "Failed to update org");
    } finally {
      setSaving(false);
    }
  }

  const backfill = async () => {
    clearBanner();
    try {
      const fn = httpsCallable(fns, "backfillOrgMembers");
      await fn({ orgId });
      await reloadMembers();
      setNotice("Members backfilled.");
    } catch (e) {
      setErr(e?.message || "Backfill failed");
    }
  };

  async function updateMorpheusLoginRequest(profile, patch = {}) {
    const profileRef = doc(db, "orgs", orgId, "morpheus", "profiles");
    const snap = await getDoc(profileRef);
    const data = snap.exists() ? (snap.data() || {}) : {};
    let items = Array.isArray(data.json) ? data.json : [];
    if (!items.length) {
      try { items = JSON.parse(String(data.value || "[]")); } catch (_) { items = []; }
    }
    const targetId = String(profile?.id || "");
    const targetEmail = String(profile?.email || profile?.userEmail || "").trim().toLowerCase();
    const next = items.map((item) => {
      const sameId = targetId && String(item?.id || "") === targetId;
      const sameEmail = targetEmail && String(item?.email || item?.userEmail || "").trim().toLowerCase() === targetEmail;
      return sameId || sameEmail ? { ...item, ...patch, updatedAt: new Date().toISOString() } : item;
    });
    await setDoc(profileRef, {
      localKey: "mc-profiles",
      value: JSON.stringify(next),
      json: next,
      updatedAt: serverTimestamp(),
      updatedByEmail: "super-admin",
    }, { merge: true });
  }

  async function approveMorpheusLoginRequest(profile) {
    const email = String(profile?.email || profile?.userEmail || "").trim().toLowerCase();
    if (!email) return setErr("This request has no email address.");
    const displayName = [profile?.name, profile?.surname].filter(Boolean).join(" ").trim() || email.split("@")[0];
    const normalizedRole = String(profile?.accessLevel || "").toLowerCase() === "owner" ? "owner" : "admin";
    clearBanner();
    setInviting(true);
    try {
      let uid = "";
      try {
        const inviteFn = httpsCallable(fns, "inviteOrgMember");
        const result = await inviteFn({ orgId, email, name: displayName, displayName, role: normalizedRole, allStores: normalizedRole === "owner", storeIds: [] });
        uid = String(result?.data?.uid || result?.data?.userId || result?.data?.authUid || result?.data?.member?.uid || "").trim();
      } catch (_) {
        await setDoc(doc(db, "orgs", orgId, "members", email), {
          email,
          displayName,
          role: normalizedRole,
          apps: ["cashup", "morpheus"],
          enabledApps: ["cashup", "morpheus"],
          appsMap: appsArrayToMap(["cashup", "morpheus"]),
          appAccess: appsArrayToMap(["cashup", "morpheus"]),
          access: appsArrayToMap(["cashup", "morpheus"]),
          permissions: appsArrayToMap(["cashup", "morpheus"]),
          allStores: normalizedRole === "owner",
          accessAllStores: normalizedRole === "owner",
          storeIds: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }

      let resetSent = false;
      try {
        const resetFn = httpsCallable(fns, "resetPasswordLink");
        const resetRes = await resetFn({ email, displayName, name: displayName, sendEmail: true });
        uid = uid || String(resetRes?.data?.uid || resetRes?.data?.userId || resetRes?.data?.authUid || "").trim();
        resetSent = true;
      } catch (_) {
        resetSent = false;
      }

      if (uid) {
        await setDoc(doc(db, "orgs", orgId, "members", uid), {
          email, uid, authUid: uid, displayName, role: normalizedRole,
          apps: ["cashup", "morpheus"], enabledApps: ["cashup", "morpheus"],
          appsMap: appsArrayToMap(["cashup", "morpheus"]), appAccess: appsArrayToMap(["cashup", "morpheus"]),
          access: appsArrayToMap(["cashup", "morpheus"]), permissions: appsArrayToMap(["cashup", "morpheus"]),
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }

      await updateMorpheusLoginRequest(profile, {
        loginInviteRequired: false,
        loginAccessStatus: "active",
        loginApprovedAt: new Date().toISOString(),
        loginRejectedAt: null,
      });
      setNotice(resetSent ? `Approved ${displayName}. A password setup/reset email was requested for ${email}.` : `Approved ${displayName}. The account was created; use Edit to send the reset-password email if it was not delivered.`);
      await reloadMembers();
    } catch (e) {
      setErr(e?.message || "Failed to approve Morpheus login access");
    } finally {
      setInviting(false);
    }
  }

  async function rejectMorpheusLoginRequest(profile) {
    clearBanner();
    try {
      await updateMorpheusLoginRequest(profile, {
        loginInviteRequired: false,
        loginAccessStatus: "rejected",
        loginRejectedAt: new Date().toISOString(),
      });
      setNotice(`Rejected personal login access for ${[profile?.name, profile?.surname].filter(Boolean).join(" ") || profile?.email}. Shared organisation login + PIN remains available.`);
      await reloadMembers();
    } catch (e) {
      setErr(e?.message || "Failed to reject Morpheus login access");
    }
  }

  const invite = async () => {
    const email = String(newEmail || "").trim().toLowerCase();
    if (!email) {
      setErr("Please enter an email.");
      return;
    }

    setInviting(true);
    setInviteLink("");
    clearBanner();

    try {
      const displayName = String(newName || "").trim() || email.split("@")[0];
      const normalizedRole = normalizeRole(newRole);
      const isMorpheusAccessRequest = morpheusLoginRequests.some((profile) => String(profile?.email || profile?.userEmail || "").trim().toLowerCase() === email);
      const selectedApps = isMorpheusAccessRequest ? ["cashup", "morpheus"] : ["cashup"];
      const allStores = normalizedRole === "owner";
      let invitedViaFallback = false;
      let invitedUid = "";
      let setupLink = "";

      try {
        const inviteFn = httpsCallable(fns, "inviteOrgMember");
        const inviteRes = await inviteFn({
          orgId,
          email,
          name: displayName,
          displayName,
          role: normalizedRole,
          allStores,
          storeIds: [],
        });
        invitedUid = String(
          inviteRes?.data?.uid ||
          inviteRes?.data?.userId ||
          inviteRes?.data?.authUid ||
          inviteRes?.data?.member?.uid ||
          ""
        ).trim();
      } catch {
        invitedViaFallback = true;
        const id = email;
        await setDoc(
          doc(db, "orgs", orgId, "members", id),
          {
            email,
            displayName,
            role: normalizedRole,
            apps: selectedApps,
            enabledApps: selectedApps,
            appsMap: appsArrayToMap(selectedApps),
            appAccess: appsArrayToMap(selectedApps),
            access: appsArrayToMap(selectedApps),
            permissions: appsArrayToMap(selectedApps),
            allStores,
            accessAllStores: allStores,
            storeIds: [],
            allowedStoreIds: [],
            visibleStoreIds: [],
            cashupStoreIds: [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      try {
        const resetFn = httpsCallable(fns, "resetPasswordLink");
        const resetRes = await resetFn({ email, displayName, name: displayName });
        invitedUid = invitedUid || String(resetRes?.data?.uid || resetRes?.data?.userId || resetRes?.data?.authUid || "").trim();
        setupLink = resetRes?.data?.link || "";
        setInviteLink(setupLink);
      } catch {
        setInviteLink("");
      }

      try {
        const backfillFn = httpsCallable(fns, "backfillOrgMembers");
        await backfillFn({ orgId });
      } catch {
        // ignore and continue with frontend mirrors
      }

      await reloadMembers();

      const refreshedMember = (members || []).find((m) => String(m.email || "").toLowerCase() === email);
      invitedUid = invitedUid || String(refreshedMember?.uid || refreshedMember?.userId || refreshedMember?.authUid || "").trim();

      if (!invitedUid) {
        try {
          const snap = await getDocs(collection(db, "orgs", orgId, "members"));
          const exact = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .find((m) => String(m.email || "").toLowerCase() === email);
          invitedUid = String(exact?.uid || exact?.userId || exact?.authUid || "").trim();
        } catch {
          // ignore
        }
      }

      if (invitedUid) {
        await setDoc(doc(db, "orgs", orgId, "members", invitedUid), {
          email,
          uid: invitedUid,
          authUid: invitedUid,
          displayName,
          role: normalizedRole,
          apps: selectedApps,
          enabledApps: selectedApps,
          appsMap: appsArrayToMap(selectedApps),
          appAccess: appsArrayToMap(selectedApps),
          access: appsArrayToMap(selectedApps),
          permissions: appsArrayToMap(selectedApps),
          allStores,
          accessAllStores: allStores,
          storeIds: [],
          allowedStoreIds: [],
          visibleStoreIds: [],
          cashupStoreIds: [],
          updatedAt: serverTimestamp(),
        }, { merge: true });

        await syncMorpheusProfileForMember({ orgId, email, displayName, apps: selectedApps });
        await syncLegacyAccessDocs({
          uid: invitedUid,
          email,
          displayName,
          role: normalizedRole,
          orgId,
          allStores,
          storeIds: [],
          apps: selectedApps,
        });
      }

      setNotice(
        Boolean(setupLink)
          ? "User added. Share the password setup link below."
          : invitedViaFallback
            ? "User added. Open Edit and click 'Send reset password email' to onboard this user."
            : "User added. If no setup link appears, open Edit and send a reset password email."
      );

      setNewEmail("");
      setNewName("");
      await reloadMembers();
    } catch (e) {
      setErr(e?.message || "Failed to invite");
    } finally {
      setInviting(false);
    }
  };

  async function openTabsEditor(m) {
    if (!orgId || !m?.id) return;
    const role = String(m.role || "viewer").toLowerCase();
    const defaultsRaw = roleTabDefaults?.[role] || DEFAULT_ROLE_TAB_DEFAULTS[role] || CASHUP_TAB_KEYS;

    const toMap = (arrOrMap) => {
      if (Array.isArray(arrOrMap)) {
        const out = {};
        CASHUP_TAB_KEYS.forEach((k) => (out[k] = arrOrMap.includes(k)));
        return out;
      }
      if (arrOrMap && typeof arrOrMap === "object") return arrOrMap;
      return {};
    };

    const defaults = toMap(defaultsRaw);

    try {
      const snap = await getDoc(doc(db, "orgs", orgId, "members", m.id));
      const raw = snap.exists() ? (snap.data()?.tabsAllowed || snap.data()?.tabs || {}) : {};
      const existing = toMap(raw);
      setTabsDraft({ ...defaults, ...existing });
    } catch {
      setTabsDraft({ ...defaults });
    }

    setTabsModalUser(m);
    setTabsModalOpen(true);
  }

  async function saveUserTabs() {
    if (!orgId || !tabsModalUser?.id) return;
    setSavingTabs(true);
    clearBanner();
    try {
      await setDoc(
        doc(db, "orgs", orgId, "members", tabsModalUser.id),
        { tabsAllowed: Object.keys(tabsDraft).filter((k) => tabsDraft[k]), updatedAt: serverTimestamp() },
        { merge: true }
      );
      setTabsModalOpen(false);
      setTabsModalUser(null);
      setNotice("Saved!");
    } catch (e) {
      setErr(e?.message || "Failed to save tab permissions");
    } finally {
      setSavingTabs(false);
    }
  }

  function openMemberEditor(m) {
    const memberStoreIds = uniqStrings(m?.storeIds || m?.allowedStoreIds || m?.visibleStoreIds || m?.cashupStoreIds || []);
    const hasExplicitAll = m?.allStores === true || m?.accessAllStores === true;
    setEditMember(m);
    setEditDisplayName((m?.displayName || m?.name || m?.label || "").trim());
    setEditRole(normalizeRole(m?.role || (m?.isAdmin ? "admin" : "viewer")));
    setEditApps(normalizeApps(m?.apps || ["cashup"]));
    setEditLeagueRole(normalizeLeagueRole(m?.leagueRole || m?.leagueAccess?.role || "viewer"));
    setEditStoreIds(memberStoreIds);
    setEditAllStores(hasExplicitAll || normalizeRole(m?.role) === "owner");
    setEditResetLoading(false);
    setEditResetStatus("");
    setEditTrialStatus("");
    setEditContractStatus("");
    setEditTrialLink("");
    setEditContractLink("");
    setShowDeleteConfirm(false);
    setEditMemberOpen(true);
  }

  async function sendResetEmailForEditMember() {
    if (!editMember?.email) return;
    setEditResetLoading(true);
    setEditResetStatus("");
    try {
      const email = String(editMember.email || "").trim().toLowerCase();
      const displayName =
        String(editDisplayName || editMember.displayName || editMember.name || email).trim() || email;

      const sendFn = httpsCallable(fns, "sendPasswordSetupEmail");
      const sendRes = await sendFn({
        email,
        displayName,
        name: displayName,
      });

      const backendLink = String(sendRes?.data?.link || "").trim();
      if (backendLink) {
        setInviteLink(backendLink);
        setEditResetStatus("Password reset email sent. Setup link also generated below as fallback.");
      } else {
        setEditResetStatus("Password reset email sent.");
      }
    } catch (e) {
      console.error("[SuperOrgDetails] sendPasswordSetupEmail failed", e);
      setEditResetStatus(`Could not send reset email: ${e?.message || "Unknown error"}`);
    } finally {
      setEditResetLoading(false);
    }
  }

  function toggleEditStore(id) {
    setEditStoreIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function saveMemberEdits() {
    if (!editMember?.id || !orgId) return;
    setSavingMember(true);
    clearBanner();
    try {
      const email = (editMember.email || "").trim().toLowerCase();
      const displayName = (editDisplayName || "").trim();
      const role = normalizeRole(editRole);
      const uid = editMember.uid || editMember.userId || editMember.authUid || "";
      const selectedStoreIds = uniqStrings(editStoreIds);
      const selectedApps = normalizeApps(editApps);
      const leagueRole = selectedApps.includes("league") ? normalizeLeagueRole(editLeagueRole) : null;
      const leaguePermissions = leagueRole ? leaguePermissionsForRole(leagueRole) : {};
      const allStores = role === "owner" ? true : editAllStores;

      if (uid) {
        try {
          const fn = httpsCallable(fns, "updateOrgMember");
          await fn({
            orgId,
            uid,
            email,
            memberId: editMember.id,
            displayName,
            role,
            patch: {
              displayName,
              role,
              apps: selectedApps,
              enabledApps: selectedApps,
              appsMap: appsArrayToMap(selectedApps),
              appAccess: appsArrayToMap(selectedApps),
              access: appsArrayToMap(selectedApps),
              permissions: appsArrayToMap(selectedApps),
              leagueRole,
              leaguePermissions,
              leagueAccess: { enabled: selectedApps.includes("league"), role: leagueRole, permissions: leaguePermissions },
              allStores,
              accessAllStores: allStores,
              storeIds: allStores ? [] : selectedStoreIds,
              allowedStoreIds: allStores ? [] : selectedStoreIds,
              visibleStoreIds: allStores ? [] : selectedStoreIds,
              cashupStoreIds: allStores ? [] : selectedStoreIds,
            },
          });
        } catch (e) {
          console.error("updateOrgMember callable failed; proceeding with Firestore", e);
        }
      }

      const ref = doc(db, "orgs", orgId, "members", editMember.id);
      const payload = {
        email,
        displayName,
        role,
        apps: selectedApps,
        enabledApps: selectedApps,
        appsMap: appsArrayToMap(selectedApps),
        appAccess: appsArrayToMap(selectedApps),
        access: appsArrayToMap(selectedApps),
        permissions: appsArrayToMap(selectedApps),
        leagueRole,
        leaguePermissions,
        leagueAccess: { enabled: selectedApps.includes("league"), role: leagueRole, permissions: leaguePermissions },
        allStores,
        accessAllStores: allStores,
        storeIds: allStores ? [] : selectedStoreIds,
        allowedStoreIds: allStores ? [] : selectedStoreIds,
        visibleStoreIds: allStores ? [] : selectedStoreIds,
        cashupStoreIds: allStores ? [] : selectedStoreIds,
        updatedAt: serverTimestamp(),
      };
      if (uid) payload.uid = uid;

      await setDoc(ref, payload, { merge: true });

      await syncMorpheusProfileForMember({ orgId, email, displayName, apps: selectedApps });

      if (uid) {
        await syncLegacyAccessDocs({
          uid,
          email,
          displayName,
          role,
          orgId,
          allStores,
          storeIds: allStores ? [] : selectedStoreIds,
          apps: selectedApps,
        });
      }

      setMembers((prev) =>
        prev.map((m) =>
          m.id === editMember.id
            ? { ...m, email, displayName, role, apps: selectedApps, enabledApps: selectedApps, appsMap: appsArrayToMap(selectedApps), appAccess: appsArrayToMap(selectedApps), access: appsArrayToMap(selectedApps), permissions: appsArrayToMap(selectedApps), leagueRole, leaguePermissions, leagueAccess: { enabled: selectedApps.includes("league"), role: leagueRole, permissions: leaguePermissions }, uid: uid || m.uid || "", allStores, storeIds: allStores ? [] : selectedStoreIds }
            : m
        )
      );

      setEditMemberOpen(false);
      setEditMember(null);
      setNotice("Saved!");
    } catch (e) {
      setErr(e?.message || "Failed to update member.");
    } finally {
      setSavingMember(false);
    }
  }

  async function confirmDeleteEditMember() {
    if (!editMember?.id || !orgId) return;
    setSavingMember(true);
    clearBanner();
    try {
      const uid = editMember.uid || editMember.userId || editMember.authUid || "";
      const archivedAt = serverTimestamp();
      const archivePayload = {
        archived: true,
        isArchived: true,
        disabled: true,
        deleted: true,
        removed: true,
        active: false,
        status: "archived",
        archivedAt,
        updatedAt: archivedAt,
      };

      await setDoc(doc(db, "orgs", orgId, "members", editMember.id), archivePayload, { merge: true });

      if (uid) {
        try {
          await setDoc(doc(db, "managers", uid), {
            archived: true,
            disabled: true,
            removed: true,
            active: false,
            status: "archived",
            archivedAt,
            updatedAt: archivedAt,
          }, { merge: true });
        } catch {}
        try {
          await setDoc(doc(db, "userRoles", uid), {
            archived: true,
            disabled: true,
            removed: true,
            active: false,
            status: "archived",
            archivedAt,
            updatedAt: archivedAt,
          }, { merge: true });
        } catch {}
      }

      setMembers((prev) => prev.filter((m) => m.id !== editMember.id));
      setShowDeleteConfirm(false);
      setEditMemberOpen(false);
      setEditMember(null);
      setNotice("Member archived.");
    } catch (e) {
      setErr(e?.message || "Failed to archive member.");
    } finally {
      setSavingMember(false);
    }
  }

  return (
    <section className="space-y-6">
      <div className="rounded border border-white/10 bg-white/5 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-white/70">Organization:</div>
          <button
            onClick={() => nav(`/super/download?orgId=${orgId}`)}
            className="text-xs px-3 py-2 rounded bg-white/10 hover:bg-white/20"
            title="Open Download for this org"
          >
            Download
          </button>
        </div>

        {loadingOrg ? (
          <div>Loading…</div>
        ) : !org ? (
          <div>Org not found.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs opacity-70">Name</label>
              <input
                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs opacity-70">Timezone</label>
              <select
                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                <option value="Africa/Johannesburg">Africa/Johannesburg</option>
              </select>
            </div>

            <div>
              <label className="block text-xs opacity-70">Province</label>
              <select
                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2"
                value={province || ""}
                onChange={(e) => setProvince(e.target.value)}
              >
                <option value="">—</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs opacity-70">Business mode</label>
              <select
                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2"
                value={businessMode}
                onChange={(e) => {
                  const nextMode = e.target.value;
                  setBusinessMode(nextMode);
                  if (["security_services", "cleaning_services", "field_services"].includes(nextMode)) {
                    setAppVariant("personal_device");
                  } else {
                    setAppVariant("shared_device");
                  }
                }}
              >
                <option value="hospitality_retail">Hospitality / Retail</option>
                <option value="security_services">Security Services</option>
                <option value="cleaning_services">Cleaning Services</option>
                <option value="field_services">Field Services</option>
              </select>
            </div>

            <div>
              <label className="block text-xs opacity-70">App variant</label>
              <select
                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2"
                value={appVariant}
                onChange={(e) => setAppVariant(e.target.value)}
              >
                <option value="shared_device">Shared Device</option>
                <option value="personal_device">Personal Device</option>
              </select>
            </div>

            <div>
              <label className="block text-xs opacity-70">Default app</label>
              <select
                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2"
                value={defaultApp}
                onChange={(e) => setDefaultApp(normalizeAppId(e.target.value))}
              >
                {normalizeApps(apps).map((appId) => (
                  <option key={appId} value={appId}>
                    {MEMBER_APP_OPTIONS.find((app) => app.id === appId)?.label || appId}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 rounded border border-cyan-500/20 bg-cyan-500/5 px-3 py-3 text-xs text-cyan-100">
              <div className="font-semibold mb-1">Mode routing</div>
              <div>
                Hospitality / Retail uses the current linked-device + store PIN flow. Security, Cleaning, and Field Services switch the Neo app into the personal-device field workflow.
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs opacity-70 mb-2">Enabled apps</label>
              <div className="flex flex-wrap gap-3">
                {MEMBER_APP_OPTIONS.map((app) => {
                  const active = normalizeApps(apps).includes(app.id);
                  return (
                    <button
                      key={app.id}
                      type="button"
                      onClick={() => toggleOrgApp(app.id)}
                      className={`rounded-full border px-4 py-2 text-sm transition ${active ? "border-cyan-400/40 bg-cyan-500/15 text-cyan-100" : "border-white/10 bg-white/5 text-white/70 hover:border-cyan-400/25 hover:text-white"}`}
                    >
                      {active ? "✓ " : ""}{app.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-white/50 mt-2">
                Enable Neo, Morpheus Count, On Time, and Matrix League at org level. Individual user access is set below per member.
              </div>
            </div>

            <div className="md:col-span-2 flex gap-2">
              <button
                onClick={saveOrg}
                disabled={saving}
                className="px-4 py-2 rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save"}
              </button>

              <button
                onClick={backfill}
                className="px-3 py-2 rounded bg-white/10 hover:bg-white/20"
              >
                Backfill members
              </button>
            </div>
          </div>
        )}

        {notice ? <div className="mt-3 text-emerald-400 text-sm">{notice}</div> : null}
        {err ? <div className="mt-3 text-red-400 text-sm">{err}</div> : null}
      </div>


      <div className="rounded border border-cyan-500/20 bg-white/5 p-4">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="text-sm text-white/70">Morpheus Count POS Integration</div>
            <div className="text-xs text-white/50 mt-1">Secure POS access is controlled here. MC Config only maps sites and chooses CSV/POS behavior.</div>
          </div>
          {(() => {
            const meta = posStatusMeta(posStatus, posEnabled);
            return (
              <span className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.18em] ${meta.className}`}>
                {meta.label}
              </span>
            );
          })()}
        </div>

        {posLoading ? (
          <div className="text-sm text-white/60">Loading POS setup…</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs opacity-70">Provider</label>
              <select
                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2"
                value={posProvider}
                onChange={(e) => setPosProvider(normalizePosProvider(e.target.value))}
              >
                {POS_PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.label}{p.available ? "" : " — Planned"}</option>)}
              </select>
              <div className="text-xs text-white/45 mt-1">More POS providers can feed the same normalized MC inventory layer later.</div>
            </div>

            <div>
              <label className="block text-xs opacity-70">Secure token storage</label>
              <div className="w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-cyan-100">Firebase Functions only</div>
              <div className="text-xs text-white/45 mt-1">{getPosProvider(posProvider).setupMessage}</div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs opacity-70">Internal note</label>
              <input
                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2"
                value={posNotes}
                onChange={(e) => setPosNotes(e.target.value)}
                placeholder="Example: Awaiting Lightspeed OAuth approval / client token setup"
              />
            </div>

            <div className="md:col-span-2 rounded border border-cyan-500/20 bg-cyan-500/5 p-3 text-xs text-cyan-100">
              <div className="font-semibold mb-1">Plug-and-play flow</div>
              <div>Super connects the POS securely → MC Config maps MC sites to POS outlets → Import/Data Source syncs latest SOH → Prepared Data validates rows → Sessions starts live count.</div>
            </div>

            <div className="md:col-span-2 flex flex-wrap gap-2 items-center">
              <button
                onClick={connectLightspeedOAuth}
                disabled={posSaving}
                className="px-4 py-2 rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60"
              >
                {posSaving ? "Working…" : (getPosProvider(posProvider).available ? `Connect ${formatPosProvider(posProvider)}` : "Save planned provider")}
              </button>
              <button
                onClick={disconnectPosIntegration}
                disabled={posSaving}
                className="px-4 py-2 rounded bg-white/10 hover:bg-white/20 disabled:opacity-60"
              >
                Disable connection
              </button>
              <button
                onClick={() => savePosIntegration()}
                disabled={posSaving}
                className="px-4 py-2 rounded border border-white/10 bg-black/20 hover:bg-white/10 disabled:opacity-60"
              >
                Save note/provider
              </button>
            </div>

            <div className="md:col-span-2 text-xs text-white/45">
              Selected provider: <span className="text-cyan-200">{formatPosProvider(posProvider)}</span> · Last update: <span className="text-cyan-200">{posLastUpdated || "Not saved yet"}</span>
            </div>
          </div>
        )}
      </div>

      <div className="rounded border border-emerald-400/20 bg-emerald-500/5 p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-emerald-200">Primary Owner</div>
            <div className="mt-1 text-xs text-white/60 max-w-2xl">This is the protected organisation owner used by Morpheus. Other members may also have Owner access, but only this email is protected from replacement or downgrade inside Morpheus.</div>
          </div>
          <div className="flex gap-2 flex-wrap items-end">
            <label className="text-xs text-white/60">
              <span className="block mb-1">Owner email</span>
              <select className="min-w-[280px] rounded bg-white text-black px-3 py-2" value={primaryOwnerEmail} onChange={(e) => setPrimaryOwnerEmail(e.target.value)}>
                <option value="">Select Primary Owner</option>
                {members.filter((m) => String(m.email || '').trim()).map((m) => <option key={m.id} value={String(m.email || '').trim().toLowerCase()}>{m.displayName || m.name || m.email} · {m.email}</option>)}
              </select>
            </label>
            <button onClick={savePrimaryOwner} disabled={savingPrimaryOwner || !primaryOwnerEmail} className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50">{savingPrimaryOwner ? 'Saving…' : 'Save Primary Owner'}</button>
            <button type="button" onClick={() => setResetPrimaryOwnerPinOpen(true)} disabled={!primaryOwnerEmail || savingPrimaryOwner || resettingPrimaryOwnerPin} className="px-4 py-2 rounded border border-amber-300/30 bg-amber-400/10 text-amber-100 hover:bg-amber-400/20 disabled:opacity-50">Reset PIN</button>
          </div>
        </div>
        <div className="mt-3 grid gap-1 text-xs text-white/60">
          <div><span className="text-white/45">PIN status:</span> Protected · reset available only from Super Admin</div>
          <div><span className="text-white/45">Reset value:</span> 000000 · change required after next access</div>
          {primaryOwnerPinStatus ? <div className="text-emerald-200">{primaryOwnerPinStatus}</div> : null}
        </div>
      </div>

      <div className="rounded border border-white/10 bg-white/5 p-4">
        {morpheusLoginRequests.length ? (
          <div className="mb-4 rounded border border-cyan-500/30 bg-cyan-500/5 p-4">
            <div className="text-sm font-semibold text-cyan-200">Morpheus login access required</div>
            <div className="mt-1 text-xs text-white/60">Approve or reject personal Morpheus login access. Approval provisions the member and requests a password setup/reset email automatically. Rejection keeps shared organisation login + PIN available.</div>
            <div className="mt-3 grid gap-2">
              {morpheusLoginRequests.map((profile) => {
                const email = String(profile.email || profile.userEmail || "").trim().toLowerCase();
                const displayName = [profile.name, profile.surname].filter(Boolean).join(" ").trim();
                return <div key={profile.id || email} className="flex items-center justify-between gap-3 rounded bg-black/20 px-3 py-2">
                  <div><div className="text-sm text-white">{displayName || email}</div><div className="text-xs text-white/55">{email} · {profile.accessLevel}</div></div>
                  <div className="flex gap-2">
                    <button type="button" disabled={inviting} className="rounded bg-emerald-600 px-3 py-2 text-xs hover:bg-emerald-500 disabled:opacity-50" onClick={() => approveMorpheusLoginRequest(profile)}>Approve & send reset email</button>
                    <button type="button" disabled={inviting} className="rounded bg-rose-600/80 px-3 py-2 text-xs hover:bg-rose-500 disabled:opacity-50" onClick={() => rejectMorpheusLoginRequest(profile)}>Reject</button>
                  </div>
                </div>;
              })}
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="text-sm text-white/70">Admins & Members</div>
          <div className="flex items-center gap-2">
            <button
              onClick={reloadMembers}
              className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20"
            >
              Refresh list
            </button>
            <Link
              to={`/super/download?orgId=${orgId}`}
              className="px-4 py-2 rounded bg-white/10 hover:bg-white/15 border border-white/10"
            >
              Download
            </Link>
          </div>
        </div>

        <div id="super-member-invite" className="grid md:grid-cols-4 gap-2 mb-3">
          <input
            className="bg-black/30 border border-white/10 rounded px-3 py-2"
            placeholder="New admin email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
          <input
            className="bg-black/30 border border-white/10 rounded px-3 py-2"
            placeholder="Display name (optional)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <select
            className="bg-black/30 border border-white/10 rounded px-3 py-2"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          >
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
            <option value="viewer">Viewer</option>
          </select>
                    <button
            onClick={invite}
            disabled={inviting}
            className="px-3 py-2 rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60"
          >
            {inviting ? "Inviting…" : "Invite"}
          </button>
          <div className="md:col-span-4 text-xs text-white/55">
            New members are invited with Neo access by default. Use <b>Edit</b> on each member below to switch Neo, Morpheus Count, On Time, and Matrix League on or off per user.
          </div>
        </div>

        {inviteLink ? (
          <div className="mb-3 text-sm">
            <div className="text-white/70 mb-1">Password set / reset link:</div>
            <div className="break-all text-cyan-300">{inviteLink}</div>
          </div>
        ) : null}

        <div className="mb-3 rounded border border-cyan-500/20 bg-cyan-500/5 p-3 text-xs text-cyan-200">
          Store visibility is now controlled per user inside <b>Edit</b>. Tabs stay available, but each person should only be assigned the stores they are allowed to see in Neo Admin.
        </div>

        {loadingMembers ? (
          <div>Loading members…</div>
        ) : members.length === 0 ? (
          <div className="text-white/60">No members found.</div>
        ) : (
          <div className="space-y-2">
            {members.map((m) => {
              const allStores = m.allStores === true || normalizeRole(m.role) === "owner";
              const assignedCount = uniqStrings(m.storeIds || []).length;
              return (
                <div
                  key={m.id}
                  className="rounded border border-white/10 bg-black/20 p-3 flex items-center justify-between gap-3"
                >
                  <div>
                    <div className="font-medium">{m.displayName || m.name || m.email || m.id}</div>
                    <div className="text-xs text-white/60">{m.email}</div>
                    <div className="text-xs text-cyan-300 mt-1">
                      {allStores ? "All stores" : assignedCount > 0 ? `${assignedCount} store${assignedCount === 1 ? "" : "s"} assigned` : "No stores assigned yet"}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {normalizeApps(m.apps || ["cashup"]).map((appId) => {
                        const shortLabel = appId === "cashup" ? "NEO" : appId === "morpheus" ? "MC" : appId === "ontime" ? "ON TIME" : appId === "league" ? "LEAGUE" : appId;
                        return (
                          <span key={appId} className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2 py-1 text-[11px] uppercase tracking-[0.18em] text-cyan-100">
                            {shortLabel}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-white/70">
                      {String(m.role || (m.isAdmin ? "admin" : "admin")).toLowerCase()}
                    </div>
                    <button className="btn small" onClick={() => openMemberEditor(m)}>Edit</button>
                    <button
                      onClick={() => openTabsEditor(m)}
                      className="text-xs px-3 py-2 rounded bg-white/10 hover:bg-white/20"
                      title="Configure which Neo Panel tabs this user can access"
                    >
                      Tabs
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Confirm
        open={editMemberOpen}
        title={editMember ? `Edit member: ${editMember.email || editMember.id}` : "Edit member"}
        okText={savingMember ? "Saving…" : "Save"}
        cancelText="Cancel"
        onCancel={() => setEditMemberOpen(false)}
        onConfirm={saveMemberEdits}
        className="max-w-5xl max-h-[92vh]"
        bodyClassName="max-h-[72vh] overflow-y-auto pr-2"
      >
        <div style={{ display: "grid", gap: 10 }}>
          <label style={{ color: "#cbd5e1", fontSize: 12 }}>Display name</label>
          <input
            className="w-full rounded bg-white text-black px-3 py-2"
            value={editDisplayName}
            onChange={(e) => setEditDisplayName(e.target.value)}
            placeholder="e.g. District Six"
          />
          <label style={{ color: "#cbd5e1", fontSize: 12 }}>Role</label>
          <select
            className="w-full rounded bg-white text-black px-3 py-2"
            value={editRole}
            onChange={(e) => {
              const nextRole = e.target.value;
              setEditRole(nextRole);
              if (nextRole === "owner") setEditAllStores(true);
            }}
          >
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="viewer">Viewer</option>
          </select>
          <div style={{ color: "#94a3b8", fontSize: 12 }}>
            Email can&apos;t be edited here. To change email, remove this user and invite the new email.
          </div>

          <div style={{ marginTop: 8, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 14 }}>
            <div style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>App access</div>
            <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 10 }}>
              Turn Neo, Morpheus Count, On Time, and Matrix League on or off for this specific user.
            </div>
            <div className="flex flex-wrap gap-2">
              {MEMBER_APP_OPTIONS.map((app) => {
                const active = normalizeApps(editApps).includes(app.id);
                return (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => toggleEditApp(app.id)}
                    className={`rounded-full border px-3 py-2 text-xs transition ${active ? "border-cyan-400/40 bg-cyan-500/15 text-cyan-100" : "border-white/10 bg-white/5 text-white/70 hover:border-cyan-400/25 hover:text-white"}`}
                  >
                    {active ? "✓ " : ""}{app.label}
                  </button>
                );
              })}
            </div>
            {normalizeApps(editApps).includes("league") ? (
              <div style={{ marginTop: 14, borderRadius: 12, border: "1px solid rgba(245, 194, 66, 0.22)", background: "rgba(245, 194, 66, 0.06)", padding: 14 }}>
                <div style={{ color: "#f8df96", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Matrix League role</div>
                <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 10 }}>Controls what this member can manage inside League.</div>
                <select className="w-full rounded bg-white text-black px-3 py-2" value={editLeagueRole} onChange={(e) => setEditLeagueRole(normalizeLeagueRole(e.target.value))}>
                  {LEAGUE_ROLES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                </select>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(leaguePermissionsForRole(editLeagueRole)).filter(([, allowed]) => allowed).map(([permission]) => (
                    <span key={permission} className="rounded-full border border-yellow-300/20 bg-yellow-300/10 px-2 py-1 text-[11px] text-yellow-100">{permission.replaceAll("_", " ")}</span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div style={{ marginTop: 8, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 14 }}>
            <div style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Store visibility</div>
            <label className="flex items-center gap-2 text-sm text-white/80 mb-3">
              <input
                type="checkbox"
                checked={editRole === "owner" ? true : editAllStores}
                disabled={editRole === "owner"}
                onChange={(e) => setEditAllStores(e.target.checked)}
              />
              <span>{editRole === "owner" ? "Owner has access to all stores" : "Give this user access to all stores"}</span>
            </label>
            {!editAllStores && editRole !== "owner" ? (
              <div>
                <div className="text-xs text-white/60 mb-2">Choose which stores this user can see in Neo Admin.</div>
                <div className="max-h-48 overflow-auto rounded border border-white/10 bg-black/20 p-3 space-y-2">
                  {loadingStores ? (
                    <div className="text-xs text-white/60">Loading stores…</div>
                  ) : stores.length === 0 ? (
                    <div className="text-xs text-white/60">No stores found for this org yet.</div>
                  ) : (
                    stores.map((store) => (
                      <label key={store.id} className="flex items-center gap-2 text-sm text-white/85">
                        <input
                          type="checkbox"
                          checked={editStoreIds.includes(store.id)}
                          onChange={() => toggleEditStore(store.id)}
                        />
                        <span>{storeLabel(store)}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="text-xs text-cyan-300">This user will see every active store in Neo Admin.</div>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
            <button
              className="btn"
              onClick={sendResetEmailForEditMember}
              disabled={editResetLoading}
              type="button"
            >
              {editResetLoading ? "Sending…" : "Send reset password email"}
            </button>
            {editResetStatus ? (
              <div style={{ color: "#94a3b8", fontSize: 12 }}>{editResetStatus}</div>
            ) : null}
          </div>

          <div style={{ marginTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 14 }}>
            <div style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Trial agreement link</div>
            <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 10 }}>
              Generate a secure tokenized trial agreement link for this member. If backend email delivery is enabled for this function, the email will be queued automatically. The secure link remains below as fallback.
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button
                className="btn"
                onClick={copyTrialAgreementLinkForEditMember}
                type="button"
              >
                Generate / copy trial link
              </button>
              {editTrialStatus ? (
                <div style={{ color: "#94a3b8", fontSize: 12 }}>{editTrialStatus}</div>
              ) : null}
            </div>
            {editTrialLink ? (
              <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.08)", color: "#67e8f9", fontSize: 12, wordBreak: "break-all" }}>
                {editTrialLink}
              </div>
            ) : null}
          </div>

          <div style={{ marginTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 14 }}>
            <div style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Client contract link</div>
            <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 10 }}>
              Generate the secure client contract link for this member. If backend email delivery is enabled for this function, the email will be queued automatically. Once signed, the system emails the signed PDF to both parties.
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button
                className="btn"
                onClick={copyClientContractLinkForEditMember}
                type="button"
              >
                Generate / copy contract link
              </button>
              {editContractStatus ? (
                <div style={{ color: "#94a3b8", fontSize: 12 }}>{editContractStatus}</div>
              ) : null}
            </div>
            {editContractLink ? (
              <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.08)", color: "#67e8f9", fontSize: 12, wordBreak: "break-all" }}>
                {editContractLink}
              </div>
            ) : null}
          </div>

          <div style={{ marginTop: 16, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 14 }}>
            <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8 }}>
              Archive this member and remove their active access. The member will still appear in archives.
            </div>
            <button
              className="btn btn-danger"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={savingMember}
              type="button"
            >
              {savingMember ? "Working…" : "Archive member"}
            </button>
          </div>

          <Confirm
            open={showDeleteConfirm}
            title="Archive member"
            okText={savingMember ? "Archiving…" : "Archive"}
            cancelText="Cancel"
            destructive
            onCancel={() => {
              if (savingMember) return;
              setShowDeleteConfirm(false);
            }}
            onConfirm={confirmDeleteEditMember}
            disableOk={savingMember}
          >
            <div className="text-sm text-white/80">
              Archive <span className="font-semibold">{editMember?.email || "this member"}</span> and remove their active access from this organization?
            </div>
          </Confirm>
        </div>
      </Confirm>

      <Confirm
        open={resetPrimaryOwnerPinOpen}
        title="Reset Primary Owner PIN"
        okText={resettingPrimaryOwnerPin ? "Resetting…" : "Reset to 000000"}
        cancelText="Cancel"
        destructive
        onCancel={() => {
          if (resettingPrimaryOwnerPin) return;
          setResetPrimaryOwnerPinOpen(false);
        }}
        onConfirm={resetPrimaryOwnerPin}
        disableOk={resettingPrimaryOwnerPin || !primaryOwnerEmail}
      >
        <div className="grid gap-2 text-sm text-white/80">
          <p>Reset the protected Primary Owner PIN for <span className="font-semibold text-white">{primaryOwnerEmail || "this organisation"}</span>?</p>
          <p>The temporary PIN will be <span className="font-semibold text-amber-200">000000</span>. Morpheus will mark the profile to require a PIN change after access.</p>
        </div>
      </Confirm>

      <Confirm
        open={tabsModalOpen}
        title={
          tabsModalUser
            ? `Tab access: ${tabsModalUser.name || tabsModalUser.email || tabsModalUser.id}`
            : "Tab access"
        }
        okText={savingTabs ? "Saving…" : "Save"}
        cancelText="Cancel"
        destructive={false}
        onCancel={() => {
          if (savingTabs) return;
          setTabsModalOpen(false);
          setTabsModalUser(null);
        }}
        onOk={saveUserTabs}
        disableOk={savingTabs}
      >
        <div className="text-xs text-white/60 mb-3">Choose which Neo Panel tabs this user can access.</div>
        <div className="flex flex-wrap gap-3">
          {CASHUP_TAB_KEYS.map((t) => (
            <label key={t.key} className="flex items-center gap-2 text-xs text-white/80">
              <input
                type="checkbox"
                checked={tabsDraft?.[t.key] === true}
                onChange={(e) => {
                  const v = e.target.checked;
                  setTabsDraft((prev) => ({ ...(prev || {}), [t.key]: v }));
                }}
              />
              <span>{t.label}</span>
            </label>
          ))}
        </div>
      </Confirm>
    </section>
  );
}
